import React from "react";
import { BrowserRouter as Router, Redirect, Route } from "react-router-dom";
import "./App.css";
import { Login } from "./pages/login";
import { Dashboard } from "./pages/private/dashboard";
import { useDispatch, useSelector } from "react-redux";
import { IApplicationState } from "./state/ducks";
import { AuthReducerState, authAsync } from "./state/ducks/auth";
import { accessCleared, accessLoaded } from "./state/ducks/access";
import { ConfigProvider, configContext } from "./components/config-provider";
import { IdleWarningDialog } from "./components/idle-warning-dialog";
import { SessionEndedDialog } from "./components/session-ended-dialog";
import {
	getMyAppAccess,
	isTokenExpired,
	isTokenPastExpiry,
	readTokenTiming,
} from "./oauth";
import {
	heartbeatMs,
	idleTimeoutMs,
	idleWarningMs,
	pollEveryTicks,
	sessionEndedRedirectMs,
	timings,
	tokenLifeTimeSeconds,
} from "./config";
import {
	getIdleMs,
	getWarningSince,
	latchWarning,
	startActivityTracking,
} from "./state/utils/activity";
import { SESSION_ENDED, signalSessionEnded } from "./state/utils/session-ended";

interface PrivateRouteProps {
	component: any;
	isAuthenticated: boolean;
	[key: string]: unknown;
}

export const PrivateRoute: React.FC<PrivateRouteProps> = (props) => {
	const { component, isAuthenticated, ...rest } = props;

	const routeComponent = (props: any) =>
		isAuthenticated ? (
			React.createElement(component, props)
		) : (
			<Redirect to={{ pathname: "/" }} />
		);
	return <Route {...rest} render={routeComponent} />;
};

/**
 * Guards the window between deciding to sign out and the logout POST landing.
 * Module-level and set synchronously so "Stay signed in" cannot slip in and
 * leave the client believing it is signed in against a dead session.
 */
let signingOut = false;

const AppContent: React.FC = () => {
	const auth = useSelector<IApplicationState, AuthReducerState>(
		(state) => state.auth
	);
	const { authServer, baseURL } = React.useContext(configContext);
	const dispatch = useDispatch();
	const tokenRef = React.useRef(auth.token.accessToken);
	tokenRef.current = auth.token.accessToken;

	const isAuthenticated = auth.token.isAuthenticated;

	// Load this user's appAccess once per session. It decides which pages the
	// portal offers; the auth server stores the grant, we interpret it.
	React.useEffect(() => {
		if (!isAuthenticated) {
			dispatch(accessCleared());
			return;
		}
		let cancelled = false;
		(async () => {
			const result = await getMyAppAccess(authServer, tokenRef.current);
			if (cancelled) return;
			// A null result (403/network) yields no pages — failing closed is
			// right for an authorisation check.
			dispatch(accessLoaded(result?.access ?? {}));
		})();
		return () => {
			cancelled = true;
		};
	}, [isAuthenticated, authServer, dispatch]);

	// Track interaction, and pick up a logout performed in another tab.
	React.useEffect(() => {
		if (!isAuthenticated) return;
		signingOut = false;
		return startActivityTracking(() => signalSessionEnded("displaced"));
	}, [isAuthenticated]);

	const refresh = React.useCallback(async (): Promise<boolean> => {
		try {
			const res = await fetch(`${authServer}/token`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ token: tokenRef.current }),
			});
			if (!res.ok) {
				// 402 covers BOTH "session has ended" and "token is expired",
				// so the status alone cannot tell them apart. Ask the token.
				signalSessionEnded(
					isTokenPastExpiry(tokenRef.current) ? "expired" : "displaced"
				);
				return false;
			}
			const { token } = await res.json();
			dispatch(authAsync(token));
			return true;
		} catch {
			// Network blip. Say nothing — the token is still valid and the next
			// heartbeat will try again.
			return false;
		}
	}, [authServer, dispatch]);

	const signOutIdle = React.useCallback(() => {
		if (signingOut) return;
		// Synchronous, before any await.
		signingOut = true;
		const token = tokenRef.current;
		// Signal FIRST: the latch is one-way, so this guarantees "idle" wins.
		// POSTing first lets the poll observe the logout and report
		// "displaced", telling the user they signed in elsewhere when they
		// merely idled out.
		signalSessionEnded("idle");
		if (!token) return;
		// keepalive so the request survives the tab being closed mid-sign-out.
		// sendBeacon cannot set an Authorization header, so it is not an option.
		void fetch(`${authServer}/token/-/logout`, {
			method: "POST",
			headers: { Authorization: `Bearer ${token}` },
			keepalive: true,
		}).catch(() => {
			/* best effort — the session still lapses at its own exp */
		});
	}, [authServer]);

	React.useEffect(() => {
		if (!isAuthenticated) return;
		let ticks = 0;
		// Snapshot the configured timings for this run of the effect. config.json
		// is fetched before any child renders, so these are the real values.
		const idleTotal = idleTimeoutMs();
		const grace = idleWarningMs();
		const pollTicks = pollEveryTicks();

		const beat = async () => {
			if (signingOut) return;

			// 1. Expiry before idle: after a long sleep both are true, and
			//    "your session expired" is the accurate one.
			if (isTokenExpired(tokenRef.current)) {
				signalSessionEnded("expired");
				return;
			}

			// 2. Idle. The countdown runs from the latch, not from the activity
			//    stamp, so jiggling the mouse cannot dismiss the warning.
			const warningSince = getWarningSince();
			if (warningSince !== null) {
				if (Date.now() - warningSince >= grace) {
					signOutIdle();
					return;
				}
			} else if (getIdleMs() >= idleTotal - grace) {
				latchWarning();
			}

			// 3. Session poll, so an idle tab still learns it was displaced.
			ticks += 1;
			if (ticks % pollTicks === 0) {
				try {
					const res = await fetch(`${baseURL}/auth/session`, {
						headers: { Authorization: `Bearer ${tokenRef.current}` },
					});
					if (!res.ok) {
						// 401 + code = the session was ended. A bare 403 is the
						// portal's guard rejecting an unusable token, which for a
						// live login means it expired.
						const body = await res.json().catch(() => null);
						if (body?.code === SESSION_ENDED) {
							signalSessionEnded("displaced");
							return;
						}
						if (res.status === 403 && isTokenPastExpiry(tokenRef.current)) {
							signalSessionEnded("expired");
							return;
						}
					}
				} catch {
					// Offline or the portal is restarting. Claiming the session
					// ended on a transient blip would sign people out for nothing.
				}
			}

			// 4. Refresh on remaining lifetime rather than a fixed interval, so
			//    it self-heals after a sleep instead of missing its window. Not
			//    while the warning is up: never silently extend a session that
			//    is about to end.
			if (getWarningSince() === null) {
				const { expAt, lifeTimeMs } = readTokenTiming(tokenRef.current);
				// Half the token's real granted lifetime. Falls back to half the
				// configured request only if the claim is missing.
				const granted = lifeTimeMs > 0 ? lifeTimeMs : tokenLifeTimeSeconds() * 1000;
				const lead = granted / 2;
				if (expAt > 0 && expAt - Date.now() < lead) await refresh();
			}
		};

		const interval = setInterval(() => void beat(), heartbeatMs());
		return () => clearInterval(interval);
	}, [isAuthenticated, authServer, baseURL, refresh, signOutIdle]);

	return (
		<Router>
			<SessionEndedDialog
				idleTimeoutMinutes={timings().idleTimeoutMinutes}
				redirectMs={sessionEndedRedirectMs()}
			/>
			<IdleWarningDialog
				graceMs={idleWarningMs()}
				onStaySignedIn={refresh}
				onSignOut={signOutIdle}
			/>
			<Route exact path="/" component={Login} />
			<PrivateRoute
				path="/dashboard"
				component={Dashboard}
				isAuthenticated={!!isAuthenticated}
			/>
		</Router>
	);
};

export const App: React.FC = () => {
	return (
		<ConfigProvider>
			<AppContent />
		</ConfigProvider>
	);
};

export default App;
